import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import { SalesInvoice } from '../../interfaces/sales/salesInvoice.interface';
import { Receipt, ReceiptCreateDTO, ReceiptMethod } from '../../interfaces/sales/receipt.interface';
import { ReceiptService } from '../../services/receipt.service';
import { AccountService } from '../../services/account.service';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { useToast } from '../../context/ToastContext';

const today = () => new Date().toISOString().slice(0, 10);

const RECEIPT_METHODS: { value: ReceiptMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'MOBILE_BANKING', label: 'Mobile Banking' },
  { value: 'OTHER', label: 'Other' },
];

interface Props {
  open: boolean;
  invoice: SalesInvoice;
  onClose: () => void;
  onPosted: (r: Receipt) => void;
}

const RecordReceiptDialog: React.FC<Props> = ({ open, invoice, onClose, onPosted }) => {
  const toast = useToast();

  const [assetAccounts, setAssetAccounts] = useState<Account[]>([]);

  // Form state
  const [amount, setAmount] = useState<number>(NaN);
  const [receiptDate, setReceiptDate] = useState(today());
  const [depositAccountId, setDepositAccountId] = useState('');
  const [method, setMethod] = useState<ReceiptMethod>('CASH');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  // Computed outstanding
  const outstanding = invoice.totalAmount - invoice.paidAmount;

  // Load ASSET accounts once on mount
  useEffect(() => {
    AccountService.getAccountsByType('ASSET')
      .then((accounts: Account[]) => setAssetAccounts(accounts))
      .catch((e: unknown) => toast.showApiError(e, 'Failed to load accounts'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset form state when dialog opens (or when invoice changes mid-open)
  useEffect(() => {
    if (open) {
      setAmount(invoice.totalAmount - invoice.paidAmount);
      setReceiptDate(today());
      setDepositAccountId('');
      setMethod('CASH');
      setReference('');
    }
  }, [open, invoice]);

  const amountValid = Number.isFinite(amount) && amount > 0 && amount <= outstanding;
  const submitDisabled = saving || !amountValid || !depositAccountId;

  const handleSubmit = async () => {
    if (submitDisabled) return;

    setSaving(true);
    try {
      const dto: ReceiptCreateDTO = {
        salesInvoiceId: invoice.id,
        amount,
        receiptDate,
        method,
        reference: reference.trim() || undefined,
        depositAccountId,
      };
      const receipt = await ReceiptService.create(dto);
      toast.showSuccess('Receipt recorded successfully');
      onPosted(receipt);
      onClose();
    } catch (e) {
      toast.showApiError(e, 'Failed to record receipt');
      // Keep dialog open so user can correct the error
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Record Receipt</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {/* Outstanding summary */}
          <Box sx={{ backgroundColor: 'action.hover', p: 2, borderRadius: 1 }}>
            <Typography variant="body2">Invoice Total: {invoice.totalAmount.toFixed(2)}</Typography>
            <Typography variant="body2">Paid: {invoice.paidAmount.toFixed(2)}</Typography>
            <Typography variant="body2" fontWeight="bold">
              Outstanding: {outstanding.toFixed(2)}
            </Typography>
          </Box>

          {/* Amount */}
          <TextField
            label="Amount"
            type="number"
            value={Number.isFinite(amount) ? amount : ''}
            onChange={(e) =>
              setAmount(e.target.value === '' ? NaN : Number(e.target.value))
            }
            inputProps={{ min: 0, max: outstanding, step: 'any' }}
            fullWidth
            required
            error={Number.isFinite(amount) && !amountValid}
            helperText={
              Number.isFinite(amount) && !amountValid
                ? `Amount must be between 0 and ${outstanding.toFixed(2)}`
                : undefined
            }
          />

          {/* Receipt Date */}
          <TextField
            label="Receipt Date"
            type="date"
            value={receiptDate}
            onChange={(e) => setReceiptDate(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />

          {/* Deposit Account (ASSET accounts) */}
          <AccountAutocomplete
            accounts={assetAccounts}
            value={depositAccountId}
            onChange={setDepositAccountId}
            label="Deposit Account"
            required
          />

          {/* Payment Method */}
          <TextField
            select
            label="Method"
            value={method}
            onChange={(e) => setMethod(e.target.value as ReceiptMethod)}
            fullWidth
            required
          >
            {RECEIPT_METHODS.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </TextField>

          {/* Reference */}
          <TextField
            label="Reference (optional)"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitDisabled}
        >
          {saving ? 'Saving…' : 'Record Receipt'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordReceiptDialog;
